import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { apiJson, buildApiUrl } from './api';
import { useSEO } from './useSEO';

const dayOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function startOfWeekMonday(dateStr) {
  const d = dateStr ? new Date(dateStr) : new Date();
  if (Number.isNaN(d.getTime())) {
    const now = new Date();
    now.setUTCHours(0, 0, 0, 0);
    return startOfWeekMonday(now.toISOString().slice(0, 10));
  }
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  d.setUTCHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

function formatWeekRange(weekStart) {
  const start = new Date(weekStart);
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  return `${start.toLocaleDateString()} – ${end.toLocaleDateString()}`;
}

const emptySlot = () => ({
  id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
  day: 'Monday',
  subject: '',
  start: '',
  duration: '',
  notes: '',
  completed: false,
  fileUrls: [{ value: '', source: 'resource' }],
  rewarded: false,
});

export default function Timetable() {
  const { user, token, loading, logout } = useAuth();
  const navigate = useNavigate();

  const [timetables, setTimetables] = useState([]);
  const [title, setTitle] = useState('My revision plan');
  const [weekStart, setWeekStart] = useState(() => startOfWeekMonday());
  const [slots, setSlots] = useState([emptySlot()]);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [growth, setGrowth] = useState(null);
  const [resourceFiles, setResourceFiles] = useState([]);
  const [resourceStatus, setResourceStatus] = useState(null);
  const [fileUploadBusy, setFileUploadBusy] = useState({});

  useSEO({
    title: 'Study timetable',
    description: 'Plan and track GCSE and A-Level revision with a weekly study timetable linked to your saved resources.',
    canonicalPath: '/timetable'
  });

  const isAuthed = useMemo(() => !!token && !!user, [token, user]);
  const resourceListId = 'resource-file-options';

  useEffect(() => {
    if (loading) return;
    if (!isAuthed) {
      navigate('/login');
      return;
    }
    loadTimetables(weekStart);
    loadGrowth();
  }, [loading, isAuthed, weekStart]);

  useEffect(() => {
    if (!isAuthed) return;
    let cancelled = false;
    setResourceStatus(null);
    apiJson('/api/resources-flat', { token })
      .then((data) => {
        if (cancelled) return;
        const publicFiles = Array.isArray(data?.files)
          ? data.files.filter((f) => !/^uploads?\//i.test(f) && !/\buploads?\//i.test(f))
          : [];
        setResourceFiles(publicFiles);
      })
      .catch((err) => {
        if (cancelled) return;
        setResourceStatus(err.message || 'Failed to load resources');
      });
    return () => { cancelled = true; };
  }, [isAuthed, token]);

  const loadTimetables = async (targetWeek) => {
    try {
      const query = targetWeek ? `?weekStart=${targetWeek}` : '';
      const data = await apiJson(`/api/timetables${query}`, { token });
      setTimetables(data.timetables || []);
    } catch (err) {
      console.error('Failed to load timetables', err);
      setStatus(err.message || 'Failed to load timetables');
    }
  };

  const updateSlot = (id, key, value) => {
    setSlots((prev) => prev.map((s) => (s.id === id ? { ...s, [key]: value } : s)));
  };

  const updateSlotFile = (id, index, value) => {
    setSlots((prev) => prev.map((s) => {
      if (s.id !== id) return s;
      const nextFiles = [...(s.fileUrls || [])];
      const current = nextFiles[index] && typeof nextFiles[index] === 'object'
        ? nextFiles[index]
        : { value: '', source: 'resource' };
      if (typeof value === 'string') {
        nextFiles[index] = { ...current, value };
      } else {
        nextFiles[index] = { ...current, ...value };
      }
      return { ...s, fileUrls: nextFiles };
    }));
  };

  const addSlotFile = (id, source = 'resource') => {
    setSlots((prev) => prev.map((s) => (s.id === id ? { ...s, fileUrls: [...(s.fileUrls || []), { value: '', source }] } : s)));
  };

  const removeSlotFile = (id, index) => {
    setSlots((prev) => prev.map((s) => {
      if (s.id !== id) return s;
      const next = [...(s.fileUrls || [])];
      next.splice(index, 1);
      return { ...s, fileUrls: next.length ? next : [{ value: '', source: 'resource' }] };
    }));
  };

  const addSlot = () => setSlots((prev) => [...prev, emptySlot()]);
  const removeSlot = (id) => setSlots((prev) => prev.filter((s) => s.id !== id));

  const resolveResourceUrl = (entry) => {
    if (!entry) return '#';
    const value = typeof entry === 'string' ? entry : entry.value;
    const source = typeof entry === 'string' ? undefined : entry.source;
    if (!value) return '#';
    const trimmed = value.trim();
    const isAbsolute = /^(https?:)?\/\//i.test(trimmed) || trimmed.startsWith('mailto:');
    if (source === 'resource' || source === 'upload' || (!isAbsolute && source !== 'external')) {
      const clean = trimmed.replace(/^\/+/, '');
      return buildApiUrl(`/files/${encodeURI(clean)}`);
    }
    return trimmed;
  };

  const formatTimeRange = (start, duration) => {
    if (!start || !duration) return null;
    const parseStart = (value) => {
      const m = /^([0-9]{1,2}):([0-9]{2})/.exec(String(value).trim());
      if (!m) return null;
      const h = Math.min(23, Math.max(0, parseInt(m[1], 10)));
      const min = Math.min(59, Math.max(0, parseInt(m[2], 10)));
      return h * 60 + min;
    };
    const parseDurationMinutes = (value) => {
      const str = String(value).trim().toLowerCase();
      if (!str) return null;
      const match = str.match(/([0-9]*\.?[0-9]+)/);
      if (!match) return null;
      const num = parseFloat(match[1]);
      if (Number.isNaN(num)) return null;
      if (str.includes('m')) return num;
      return num * 60;
    };
    const startMin = parseStart(start);
    const durMin = parseDurationMinutes(duration);
    if (startMin == null || durMin == null) return null;
    const endMin = (startMin + Math.max(0, durMin)) % (24 * 60);
    const pad = (n) => String(n).padStart(2, '0');
    const toClock = (mins) => `${pad(Math.floor(mins / 60))}:${pad(mins % 60)}`;
    return `${toClock(startMin)}–${toClock(endMin)}`;
  };

  const uploadTimetableFile = async (file) => {
    const form = new FormData();
    form.append('file', file);

    const res = await fetch(buildApiUrl('/api/timetable-files'), {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });

    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      throw new Error(errText || 'Failed to upload file');
    }

    return res.json();
  };

  const handleFileUpload = async (slotId, index, file) => {
    if (!file) return;
    const key = `${slotId}-${index}`;
    setFileUploadBusy((prev) => ({ ...prev, [key]: true }));
    setStatus(null);
    try {
      const result = await uploadTimetableFile(file);
      updateSlotFile(slotId, index, { value: result.path, source: 'upload' });
      setStatus('File uploaded');
    } catch (err) {
      setStatus(err.message || 'Failed to upload file');
    } finally {
      setFileUploadBusy((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const saveTimetable = async () => {
    if (!title.trim()) {
      setStatus('Title is required');
      return;
    }
    const cleanedSlots = slots
      .map((s) => ({
        ...s,
        day: s.day.trim(),
        subject: s.subject.trim(),
        start: s.start.trim(),
        duration: s.duration.trim(),
        notes: s.notes.trim(),
        completed: !!s.completed,
        rewarded: !!s.rewarded,
        fileUrls: Array.isArray(s.fileUrls)
          ? s.fileUrls
              .map((f) => ({
                value: (f.value || '').trim(),
                source: f.source === 'external' ? 'external' : (f.source === 'upload' ? 'upload' : 'resource'),
              }))
              .filter((f) => f.value)
          : [],
      }))
      .filter((s) => s.day || s.subject || s.start || s.duration || s.notes);
    if (cleanedSlots.length === 0) {
      setStatus('Add at least one slot');
      return;
    }

    setBusy(true);
    setStatus(null);
    try {
      await apiJson('/api/timetables', {
        method: 'POST',
        token,
        body: { title, slots: cleanedSlots, weekStart },
      });
      setStatus('Saved');
      await loadTimetables(weekStart);
    } catch (err) {
      setStatus(err.message || 'Failed to save timetable');
    } finally {
      setBusy(false);
    }
  };

  const deleteTimetable = async (id) => {
    setBusy(true);
    setStatus(null);
    try {
      await apiJson(`/api/timetables/${id}`, { method: 'DELETE', token });
      await loadTimetables(weekStart);
    } catch (err) {
      setStatus(err.message || 'Failed to delete');
    } finally {
      setBusy(false);
    }
  };

  const changeWeek = (delta) => {
    const base = new Date(weekStart);
    base.setUTCDate(base.getUTCDate() + delta * 7);
    setWeekStart(startOfWeekMonday(base.toISOString().slice(0, 10)));
  };

  const loadGrowth = async () => {
    try {
      const data = await apiJson('/api/growth', { token });
      setGrowth(data.growth);
    } catch (err) {
      console.error('Failed to load growth', err);
    }
  };

  const recordGrowthEvent = async (completed) => {
    try {
      const data = await apiJson('/api/growth/event', { method: 'POST', token, body: { completed } });
      setGrowth(data.growth);
    } catch (err) {
      console.error('Failed to record growth', err);
    }
  };

  const toggleCompletion = async (timetableId, slotIndex) => {
    setBusy(true);
    setStatus(null);
    try {
      const timetable = timetables.find((t) => t.id === timetableId);
      if (!timetable) {
        setBusy(false);
        setStatus('Timetable not found');
        return;
      }
      const updatedSlots = (timetable.slots || []).map((s, idx) => {
        if (idx !== slotIndex) return s;
        const nextCompleted = !s.completed;
        return {
          ...s,
          completed: nextCompleted,
          rewarded: s.rewarded || nextCompleted, // once rewarded, stay rewarded
        };
      });
      const targetSlot = timetable.slots?.[slotIndex];
      const willReward = targetSlot && !targetSlot.completed && !targetSlot.rewarded;
      const response = await apiJson(`/api/timetables/${timetableId}`, {
        method: 'PUT',
        token,
        body: { slots: updatedSlots },
      });
      const updated = response.timetable || response;
      setTimetables((prev) => prev.map((t) => (t.id === timetableId ? updated : t)));
      if (willReward) {
        await recordGrowthEvent(true);
      }
    } catch (err) {
      setStatus(err.message || 'Failed to update slot');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="auth-wrapper"><div className="auth-card">Loading session…</div></div>;

  if (!isAuthed) return null;

  const growthLevel = growth?.level || 1;
  const stages = [
    { threshold: 1, label: 'Seed', emoji: '🌰' },
    { threshold: 5, label: 'Sprout', emoji: '🌱' },
    { threshold: 12, label: 'Bud', emoji: '🌿' },
    { threshold: 20, label: 'Bloom', emoji: '🌸' },
    { threshold: 35, label: 'Tree', emoji: '🌳' },
    { threshold: 55, label: 'Great Tree', emoji: '🌲' },
  ];
  const currentStage = stages.reduce((acc, stage) => (growthLevel >= stage.threshold ? stage : acc), stages[0]);
  const nextStage = stages.find((s) => s.threshold > growthLevel);
  const toNext = nextStage ? Math.max(0, nextStage.threshold - growthLevel) : 0;

  return (
    <div className="timetable-page">
      <div className="timetable-header">
        <div>
          <h2>Revision timetable</h2>
          <p className="muted">Signed in as {user?.email}</p>
        </div>
        <div className="timetable-actions">
          <button className="secondary-btn" onClick={() => navigate('/')}>Back to resources</button>
          <button className="secondary-btn" onClick={logout}>Sign out</button>
        </div>
      </div>

      <div className="growth-card">
        <div className="growth-emoji" aria-hidden>{currentStage.emoji}</div>
        <div>
          <p className="muted">Keep checking in and completing sessions to help your plant grow.</p>
          <div className="growth-metrics">
            <span className="pill">Stage: {currentStage.label}</span>
            <span className="pill">Level {growthLevel}</span>
            <span className="pill">Streak {growth?.streak || 0} days</span>
            {nextStage ? <span className="pill">{toNext} to {nextStage.label}</span> : <span className="pill">Maxed</span>}
          </div>
        </div>
      </div>

      <div className="auth-card" style={{ maxWidth: 960 }}>
        <div className="timetable-form-row">
          <label className="wide">
            Week starting (Monday)
            <input
              type="date"
              value={weekStart}
              onChange={(e) => e.target.value && setWeekStart(startOfWeekMonday(e.target.value))}
            />
          </label>
          <div className="week-controls">
            <button className="secondary-btn" type="button" onClick={() => changeWeek(-1)} disabled={busy}>Previous week</button>
            <span className="muted">{formatWeekRange(weekStart)}</span>
            <button className="secondary-btn" type="button" onClick={() => setWeekStart(startOfWeekMonday())} disabled={busy}>This week</button>
            <button className="secondary-btn" type="button" onClick={() => changeWeek(1)} disabled={busy}>Next week</button>
          </div>
        </div>

        <div className="timetable-form-row">
          <label className="wide">
            Plan name
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Spring term revision" />
          </label>
          <button className="primary-btn" type="button" onClick={saveTimetable} disabled={busy}>
            {busy ? 'Saving…' : 'Save timetable'}
          </button>
        </div>

        <div className="slots">
          {slots.map((slot) => (
            <div className="slot-row" key={slot.id}>
              <select value={slot.day} onChange={(e) => updateSlot(slot.id, 'day', e.target.value)}>
                <option value="">Day</option>
                {dayOptions.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <input
                placeholder="Subject"
                value={slot.subject}
                onChange={(e) => updateSlot(slot.id, 'subject', e.target.value)}
              />
              <input
                type="time"
                placeholder="Start time"
                value={slot.start}
                onChange={(e) => updateSlot(slot.id, 'start', e.target.value)}
              />
              <input
                placeholder="Duration (e.g. 1h)"
                value={slot.duration}
                onChange={(e) => updateSlot(slot.id, 'duration', e.target.value)}
              />
              <input
                placeholder="Notes"
                value={slot.notes}
                onChange={(e) => updateSlot(slot.id, 'notes', e.target.value)}
              />
              <div className="files-col">
                {(slot.fileUrls || []).map((f, idx) => {
                  const source = f.source || 'resource';
                  const uploadKey = `${slot.id}-${idx}`;
                  return (
                    <div className="file-entry" key={idx}>
                      <select
                        value={source}
                        onChange={(e) => updateSlotFile(slot.id, idx, { source: e.target.value })}
                      >
                        <option value="resource">Resource file</option>
                        <option value="external">External link</option>
                        <option value="upload">Upload file</option>
                      </select>
                      {source === 'external' ? (
                        <input
                          placeholder="https://example.com/link"
                          value={f.value || ''}
                          onChange={(e) => updateSlotFile(slot.id, idx, { value: e.target.value })}
                        />
                      ) : source === 'upload' ? (
                        <div>
                          <input
                            type="file"
                            onChange={(e) => handleFileUpload(slot.id, idx, e.target.files?.[0])}
                            disabled={!!fileUploadBusy[uploadKey] || busy}
                          />
                          <p className="muted" style={{ margin: '4px 0 0' }}>
                            {fileUploadBusy[uploadKey] ? 'Uploading…' : (f.value ? `Uploaded: ${f.value.split('/').pop()}` : 'Max 50MB, stored to this plan')}
                          </p>
                        </div>
                      ) : (
                        <input
                          list={resourceListId}
                          placeholder="a-level/Biology/AQA/Past Papers/file.pdf"
                          value={f.value || ''}
                          onChange={(e) => updateSlotFile(slot.id, idx, { value: e.target.value })}
                        />
                      )}
                      <button className="icon-btn file-remove" type="button" onClick={() => removeSlotFile(slot.id, idx)} aria-label="Remove file">✕</button>
                    </div>
                  );
                })}
                <div className="file-buttons">
                  <button className="secondary-btn" type="button" onClick={() => addSlotFile(slot.id, 'resource')}>+ Resource file</button>
                  <button className="secondary-btn" type="button" onClick={() => addSlotFile(slot.id, 'external')}>+ External link</button>
                  <button className="secondary-btn" type="button" onClick={() => addSlotFile(slot.id, 'upload')}>+ Upload file</button>
                </div>
                {resourceStatus && <p className="muted" style={{ margin: '4px 0 0' }}>{resourceStatus}</p>}
              </div>
              <button className="icon-btn remove-slot" type="button" onClick={() => removeSlot(slot.id)} aria-label="Remove slot">✕</button>
            </div>
          ))}
          <button className="secondary-btn" type="button" onClick={addSlot}>Add slot</button>
        </div>

        <datalist id={resourceListId}>
          {(resourceFiles || []).slice(0, 1000).map((file) => (
            <option key={file} value={file} />
          ))}
        </datalist>

        {status && <p className="muted" style={{ marginTop: 8 }}>{status}</p>}
      </div>

      <div className="timetable-list">
        <h3>Your saved timetables</h3>
        {timetables.length === 0 && <p className="muted">No timetables yet.</p>}
        <div className="timetable-cards">
          {timetables.map((t) => (
            <div className="timetable-card" key={t.id}>
              <div className="timetable-card-head">
                <div>
                  <h4>{t.title}</h4>
                  <p className="muted">Week of {t.weekStart ? formatWeekRange(t.weekStart) : 'Not set'}</p>
                  <p className="muted">Updated {new Date(t.updatedAt || t.createdAt).toLocaleString()}</p>
                </div>
                <button className="icon-btn" onClick={() => deleteTimetable(t.id)} aria-label="Delete timetable">🗑</button>
              </div>
              <ul className="slot-list">
                {(t.slots || []).map((s, idx) => (
                  <li key={idx} className={s.completed ? 'slot-complete' : ''}>
                    <label className="slot-toggle">
                      <input
                        type="checkbox"
                        checked={!!s.completed}
                        onChange={() => toggleCompletion(t.id, idx)}
                        disabled={busy}
                      />
                      <span>
                        <strong>{s.day || 'Any day'}</strong> – {s.subject || 'Topic'} {formatTimeRange(s.start, s.duration) ? `(${formatTimeRange(s.start, s.duration)})` : `(${s.start || 'start'}, ${s.duration || 'duration'})`} {s.notes ? `– ${s.notes}` : ''}
                        {s.completed ? ' ✓' : ''}
                      </span>
                    </label>
                      {s.fileUrls?.length ? (
                      <div className="slot-files">
                        {s.fileUrls.map((entry, fileIdx) => {
                          const entrySource = typeof entry === 'string' ? '' : entry?.source;
                          const isUpload = (entrySource || '').toLowerCase() === 'upload';
                          return isUpload ? (
                            <span key={fileIdx} className="pill-link private-pill" aria-label="Private upload">🔒 Private upload</span>
                          ) : (
                            <a key={fileIdx} href={resolveResourceUrl(entry)} target="_blank" rel="noreferrer" className="pill-link">📄 Resource {fileIdx + 1}</a>
                          );
                        })}
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
